/**
 * useBarcodeScanner Hook
 * 
 * Handles barcode scanning from USB scanners (keyboard emulation)
 * and camera-based scanning.
 * 
 * USB Scanner Usage:
 * const { lastScan, isListening, startListening, stopListening } = useBarcodeScanner({
 *   onScan: (code) => handleProductSearch(code),
 * });
 * 
 * Camera Scanner Usage:
 * const { startCameraScan, stopCameraScan, isCameraActive } = useBarcodeScanner({
 *   onScan: (code) => handleProductSearch(code),
 *   enableCamera: true,
 * });
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { createScannerDetector, detectBarcodeType, validateBarcode } from '../utils/barcode';

export function useBarcodeScanner(options = {}) {
  const {
    onScan = () => {},
    onError = () => {},
    onManualInput = () => {},
    autoStart = true,
    validateOnScan = true,
    targetElement = null, // If null, listens on document
    maxInputTime = 50,
    minLength = 3,
    enableCamera = false,
    cameraFacing = 'environment', // 'user' or 'environment'
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [lastScanTime, setLastScanTime] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  
  const scannerRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const animationRef = useRef(null);

  // Initialize scanner detector
  useEffect(() => {
    scannerRef.current = createScannerDetector({
      maxInputTime,
      minLength,
      onScan: (code) => {
        const type = detectBarcodeType(code);
        const isValid = validateOnScan ? validateBarcode(code, type) : true;
        
        if (isValid) {
          const scanData = {
            code,
            type,
            timestamp: new Date(),
            source: 'scanner',
          };
          setLastScan(scanData);
          setLastScanTime(new Date());
          onScan(code, scanData);
        } else {
          onError({ code, type, error: 'Invalid barcode' });
        }
      },
      onInput: (text) => {
        onManualInput(text);
      },
    });
  }, [maxInputTime, minLength, validateOnScan, onScan, onError, onManualInput]);

  // Handle keyboard events
  const handleKeyDown = useCallback((event) => {
    if (!scannerRef.current) return;
    
    // Ignore if user is typing in an input field (unless it's our target)
    if (targetElement === null) {
      const tagName = event.target.tagName.toLowerCase();
      const isInputField = tagName === 'input' || tagName === 'textarea' || event.target.isContentEditable;
      
      // Allow scanning even in input fields if it's a rapid scan
      if (isInputField && event.key !== 'Enter') {
        // Let the scanner detector handle it
        scannerRef.current.handleKeyPress(event);
        return;
      }
    }
    
    scannerRef.current.handleKeyPress(event);
  }, [targetElement]);

  // Start listening for scans
  const startListening = useCallback(() => {
    const target = targetElement || document;
    target.addEventListener('keydown', handleKeyDown);
    setIsListening(true);
  }, [handleKeyDown, targetElement]);

  // Stop listening for scans
  const stopListening = useCallback(() => {
    const target = targetElement || document;
    target.removeEventListener('keydown', handleKeyDown);
    setIsListening(false);
    if (scannerRef.current) {
      scannerRef.current.reset();
    }
  }, [handleKeyDown, targetElement]);

  // Auto-start listening
  useEffect(() => {
    if (autoStart) {
      startListening();
    }
    return () => stopListening();
  }, [autoStart, startListening, stopListening]);

  // Camera scanning (using BarcodeDetector API if available)
  const startCameraScan = useCallback(async (videoElement = null) => {
    if (!enableCamera) {
      onError({ error: 'Camera scanning not enabled' });
      return false;
    }

    try {
      // Check for BarcodeDetector API support
      if (!('BarcodeDetector' in window)) {
        setCameraError('BarcodeDetector API not supported in this browser');
        onError({ error: 'BarcodeDetector API not supported' });
        return false;
      }

      // Get camera stream
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: cameraFacing,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;

      // Set up video element
      const video = videoElement || videoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play();
      }

      setIsCameraActive(true);
      setCameraError(null);

      // Create barcode detector
      const barcodeDetector = new window.BarcodeDetector({
        formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'qr_code'],
      });

      // Start detection loop
      const detectBarcode = async () => {
        if (!streamRef.current || !video) return;

        try {
          const barcodes = await barcodeDetector.detect(video);
          
          if (barcodes.length > 0) {
            const barcode = barcodes[0];
            const code = barcode.rawValue;
            const type = detectBarcodeType(code);
            
            const scanData = {
              code,
              type,
              timestamp: new Date(),
              source: 'camera',
              format: barcode.format,
            };
            
            setLastScan(scanData);
            setLastScanTime(new Date());
            onScan(code, scanData);
          }
        } catch (err) {
          // Detection error, continue scanning
        }

        if (isCameraActive) {
          animationRef.current = requestAnimationFrame(detectBarcode);
        }
      };

      animationRef.current = requestAnimationFrame(detectBarcode);
      return true;

    } catch (err) {
      setCameraError(err.message);
      onError({ error: err.message });
      return false;
    }
  }, [enableCamera, cameraFacing, onScan, onError, isCameraActive]);

  // Stop camera scanning
  const stopCameraScan = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsCameraActive(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopListening();
      stopCameraScan();
    };
  }, [stopListening, stopCameraScan]);

  // Manual scan input
  const manualScan = useCallback((code) => {
    if (!code) return;
    
    const type = detectBarcodeType(code);
    const isValid = validateOnScan ? validateBarcode(code, type) : true;
    
    if (isValid) {
      const scanData = {
        code,
        type,
        timestamp: new Date(),
        source: 'manual',
      };
      setLastScan(scanData);
      setLastScanTime(new Date());
      onScan(code, scanData);
    } else {
      onError({ code, type, error: 'Invalid barcode' });
    }
  }, [validateOnScan, onScan, onError]);

  // Clear last scan
  const clearLastScan = useCallback(() => {
    setLastScan(null);
    setLastScanTime(null);
  }, []);

  return {
    // State
    isListening,
    lastScan,
    lastScanTime,
    isCameraActive,
    cameraError,
    
    // USB Scanner
    startListening,
    stopListening,
    
    // Camera Scanner
    startCameraScan,
    stopCameraScan,
    videoRef,
    
    // Manual
    manualScan,
    clearLastScan,
  };
}

export default useBarcodeScanner;
